<template>
  <div class="page">
    <div class="container">
      <div class="page-header">
        <h1>RED Bills</h1>
        <div class="header-actions">
          <Button icon="pi pi-eye" label="Preview PDF" severity="secondary" @click="previewPdf" :loading="previewing" />
          <Button icon="pi pi-download" label="Download PDF" severity="danger" @click="downloadPdf" :loading="downloading" />
        </div>
      </div>

      <!-- Year Filter Controls -->
      <div class="filter-controls">
        <div class="filter-group">
          <label>Start Year</label>
          <Select v-model="yearStart" :options="yearOptions" optionLabel="label" optionValue="value" placeholder="Start Year" />
        </div>
        <div class="filter-group">
          <label>End Year</label>
          <Select v-model="yearEnd" :options="yearOptions" optionLabel="label" optionValue="value" placeholder="End Year" />
        </div>
        <div class="filter-group search-group">
          <label>Search</label>
          <InputText v-model="searchFilter" placeholder="Filter by subject title, position paper, salient features..." />
        </div>
      </div>

      <!-- Tabs -->
      <div class="tab-controls">
        <Button :label="'Summary'" :severity="activeTab === 'summary' ? undefined : 'secondary'" @click="activeTab = 'summary'" />
        <Button :label="'Table'" :severity="activeTab === 'table' ? undefined : 'secondary'" @click="activeTab = 'table'" />
      </div>

      <!-- Summary Tab -->
      <div v-if="activeTab === 'summary'" class="summary-section">
        <div class="summary-loading" v-if="summaryLoading">Loading summary...</div>
        <table v-else class="summary-table">
          <thead>
            <tr>
              <th>Measure</th>
              <th>No. of Bills</th>
              <th>%</th>
              <th>Estimated Revenue Impact (in Billions)</th>
              <th>Estimated Budget Impact (in Billions)</th>
              <th>Estimated Total Fiscal Impact (in Billions)</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(cat, idx) in summaryData.categories" :key="idx">
              <tr class="group-header">
                <td colspan="6"><strong>{{ cat.label }}</strong></td>
              </tr>
              <tr>
                <td class="indent">Revenue Eroding</td>
                <td class="center">{{ cat.eroding.count }}</td>
                <td class="center">{{ cat.eroding.percent }}%</td>
                <td :class="['right', { negative: cat.eroding.revenue < 0 }]">{{ formatBillions(cat.eroding.revenue) }}</td>
                <td :class="['right', { negative: cat.eroding.budgetary < 0 }]">{{ formatBillions(cat.eroding.budgetary) }}</td>
                <td :class="['right', { negative: cat.eroding.fiscal < 0 }]">{{ formatBillions(cat.eroding.fiscal) }}</td>
              </tr>
              <tr>
                <td class="indent">Revenue Gain</td>
                <td class="center">{{ cat.gain.count }}</td>
                <td class="center">{{ cat.gain.percent }}%</td>
                <td class="right">{{ formatBillions(cat.gain.revenue) }}</td>
                <td class="right">{{ formatBillions(cat.gain.budgetary) }}</td>
                <td class="right">{{ formatBillions(cat.gain.fiscal) }}</td>
              </tr>
            </template>
            <!-- TOTAL -->
            <tr class="total-header">
              <td colspan="6"><strong>TOTAL</strong></td>
            </tr>
            <tr class="total-row">
              <td class="indent">Revenue Eroding</td>
              <td class="center">{{ summaryData.total?.eroding?.count || 0 }}</td>
              <td class="center">{{ summaryData.total?.eroding?.percent || 0 }}%</td>
              <td :class="['right', { negative: (summaryData.total?.eroding?.revenue || 0) < 0 }]">{{ formatBillions(summaryData.total?.eroding?.revenue) }}</td>
              <td :class="['right', { negative: (summaryData.total?.eroding?.budgetary || 0) < 0 }]">{{ formatBillions(summaryData.total?.eroding?.budgetary) }}</td>
              <td :class="['right', { negative: (summaryData.total?.eroding?.fiscal || 0) < 0 }]">{{ formatBillions(summaryData.total?.eroding?.fiscal) }}</td>
            </tr>
            <tr class="total-row">
              <td class="indent">Revenue Gain</td>
              <td class="center">{{ summaryData.total?.gain?.count || 0 }}</td>
              <td class="center">{{ summaryData.total?.gain?.percent || 0 }}%</td>
              <td class="right">{{ formatBillions(summaryData.total?.gain?.revenue) }}</td>
              <td class="right">{{ formatBillions(summaryData.total?.gain?.budgetary) }}</td>
              <td class="right">{{ formatBillions(summaryData.total?.gain?.fiscal) }}</td>
            </tr>
            <!-- NET TOTAL -->
            <tr class="net-total-row">
              <td colspan="5" class="right"><strong>NET TOTAL (Gain/Loss)</strong></td>
              <td :class="['right', 'net-total-value', { negative: (summaryData.net_total || 0) < 0 }]">{{ formatBillions(summaryData.net_total) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Table Tab -->
      <div v-if="activeTab === 'table'">
      <DataTable
        :value="filteredRedBills"
        :loading="loading"
        :paginator="true"
        :rows="10"
        :rowsPerPageOptions="[10, 25, 50, 100]"
        stripedRows
        showGridlines
        scrollable
        scrollHeight="70vh"
        class="red-bills-table"
      >
        <ColumnGroup type="header">
          <Row>
            <Column header="No." :rowspan="2" />
            <Column header="Subject Title" :rowspan="2" field="subject_title" sortable />
            <Column header="Position Paper" :rowspan="2" />
            <Column header="Type" :rowspan="2" />
            <Column header="Budgetary Impact" :colspan="budgetaryColspan" />
            <Column header="Revenue Impact" :colspan="revenueColspan" />
            <Column header="Fiscal Impact Total" :rowspan="2" field="fiscal_impact_total" sortable />
          </Row>
          <Row>
            <Column header="Salient Features / Particulars" />
            <Column v-for="year in dynamicYears" :key="'bh-' + year" :header="String(year)" />
            <Column header="Sum" field="budgetary.sum" sortable />
            <Column header="Average" />
            <Column header="Salient Features / Particulars" />
            <Column v-for="year in dynamicYears" :key="'rh-' + year" :header="String(year)" />
            <Column header="Sum" field="revenue.sum" sortable />
            <Column header="Average" />
          </Row>
        </ColumnGroup>

        <!-- Body columns must match header count exactly -->
        <Column field="no" style="min-width: 60px; text-align: center;">
          <template #body="{ data }">{{ data.no }}</template>
        </Column>

        <Column field="subject_title" sortable style="min-width: 200px;">
          <template #body="{ data }">
            <span class="subject-title">{{ data.subject_title }}</span>
          </template>
        </Column>

        <Column style="min-width: 200px;">
          <template #body="{ data }">
            <div class="pp-links" v-if="data.position_papers && data.position_papers.length">
              <div v-for="(pp, idx) in data.position_papers" :key="idx" class="pp-item">
                <span :class="['source-tag', 'source-' + (pp.source || '').toLowerCase()]">{{ pp.source }}</span>
                <span v-if="pp.title" class="pp-title">{{ pp.title }}</span>
                <div class="pp-link-row">
                  <a v-if="pp.draft_position_paper_link" :href="pp.draft_position_paper_link" target="_blank" class="pp-link">
                    <i class="pi pi-file"></i> Draft
                  </a>
                  <a v-if="pp.signed_position_paper_link" :href="pp.signed_position_paper_link" target="_blank" class="pp-link signed">
                    <i class="pi pi-check-circle"></i> Signed
                  </a>
                </div>
              </div>
            </div>
            <span v-else class="no-data">—</span>
          </template>
        </Column>

        <Column style="min-width: 120px;">
          <template #body="{ data }">
            <div v-if="data.bill_category && data.bill_category.length">
              <span v-for="(cat, idx) in data.bill_category" :key="idx" class="category-tag">{{ cat }}</span>
            </div>
            <span v-else class="no-data">—</span>
          </template>
        </Column>

        <!-- Budgetary: Salient Features -->
        <Column style="min-width: 200px;">
          <template #body="{ data }">
            <div v-if="data.budgetary?.particulars_sources?.length" class="particulars-list">
              <div v-for="(src, idx) in data.budgetary.particulars_sources" :key="idx" class="particulars-item">
                <span :class="['source-tag', 'source-' + src.source.toLowerCase()]">{{ src.source }}</span>
                <span class="particulars-text">{{ src.text }}</span>
              </div>
            </div>
            <span v-else class="no-data">—</span>
          </template>
        </Column>

        <!-- Budgetary: Year Columns -->
        <Column v-for="year in dynamicYears" :key="'bv-' + year" style="min-width: 120px;">
          <template #body="{ data }">
            <div class="amount-cell">{{ formatAmount(data.budgetary?.year_amounts?.[year]) }}</div>
          </template>
        </Column>

        <!-- Budgetary: Sum -->
        <Column field="budgetary.sum" sortable style="min-width: 120px;">
          <template #body="{ data }">
            <div class="amount-cell sum-cell">{{ formatAmount(data.budgetary?.sum) }}</div>
          </template>
        </Column>

        <!-- Budgetary: Average -->
        <Column style="min-width: 120px;">
          <template #body="{ data }">
            <div class="amount-cell">{{ formatAverage(data.budgetary?.average) }}</div>
          </template>
        </Column>

        <!-- Revenue: Salient Features -->
        <Column style="min-width: 200px;">
          <template #body="{ data }">
            <div v-if="data.revenue?.particulars_sources?.length" class="particulars-list">
              <div v-for="(src, idx) in data.revenue.particulars_sources" :key="idx" class="particulars-item">
                <span :class="['source-tag', 'source-' + src.source.toLowerCase()]">{{ src.source }}</span>
                <span class="particulars-text">{{ src.text }}</span>
              </div>
            </div>
            <span v-else class="no-data">—</span>
          </template>
        </Column>

        <!-- Revenue: Year Columns -->
        <Column v-for="year in dynamicYears" :key="'rv-' + year" style="min-width: 120px;">
          <template #body="{ data }">
            <div class="amount-cell">{{ formatAmount(data.revenue?.year_amounts?.[year]) }}</div>
          </template>
        </Column>

        <!-- Revenue: Sum -->
        <Column field="revenue.sum" sortable style="min-width: 120px;">
          <template #body="{ data }">
            <div class="amount-cell sum-cell">{{ formatAmount(data.revenue?.sum) }}</div>
          </template>
        </Column>

        <!-- Revenue: Average -->
        <Column style="min-width: 120px;">
          <template #body="{ data }">
            <div class="amount-cell">{{ formatAverage(data.revenue?.average) }}</div>
          </template>
        </Column>

        <!-- Fiscal Impact Total -->
        <Column field="fiscal_impact_total" sortable style="min-width: 140px;">
          <template #body="{ data }">
            <div class="amount-cell fiscal-total">{{ formatAmount(data.fiscal_impact_total) }}</div>
          </template>
        </Column>
      </DataTable>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import ColumnGroup from 'primevue/columngroup'
import Row from 'primevue/row'
import Button from 'primevue/button'
import Select from 'primevue/select'
import InputText from 'primevue/inputtext'
import api from '../axios.js'

const redBills = ref([])
const loading = ref(false)
const yearStart = ref(2026)
const yearEnd = ref(2028)
const searchFilter = ref('')
const previewing = ref(false)
const downloading = ref(false)
const activeTab = ref('summary')
const summaryData = ref({ categories: [], total: {}, net_total: 0 })
const summaryLoading = ref(false)

const filteredRedBills = computed(() => {
  if (!searchFilter.value) return redBills.value
  const q = searchFilter.value.toLowerCase()
  return redBills.value.filter(bill => {
    const subjectMatch = (bill.subject_title || '').toLowerCase().includes(q)
    const ppMatch = (bill.position_papers || []).some(pp =>
      (pp.title || '').toLowerCase().includes(q)
    )
    const budSalientMatch = (bill.budgetary?.particulars_sources || []).some(s =>
      (s.text || '').toLowerCase().includes(q)
    )
    const revSalientMatch = (bill.revenue?.particulars_sources || []).some(s =>
      (s.text || '').toLowerCase().includes(q)
    )
    return subjectMatch || ppMatch || budSalientMatch || revSalientMatch
  })
})

const yearOptions = ref([
  { label: '2020', value: 2020 },
  { label: '2021', value: 2021 },
  { label: '2022', value: 2022 },
  { label: '2023', value: 2023 },
  { label: '2024', value: 2024 },
  { label: '2025', value: 2025 },
  { label: '2026', value: 2026 },
  { label: '2027', value: 2027 },
  { label: '2028', value: 2028 },
  { label: '2029', value: 2029 },
  { label: '2030', value: 2030 },
])

const dynamicYears = computed(() => {
  const start = yearStart.value
  const end = yearEnd.value
  if (!start || !end || start > end) return []
  const years = []
  for (let y = start; y <= end; y++) years.push(y)
  return years
})

// Colspan = Salient Features (1) + Years (n) + Sum (1) + Average (1)
const budgetaryColspan = computed(() => dynamicYears.value.length + 3)
const revenueColspan = computed(() => dynamicYears.value.length + 3)

const loadRedBills = async () => {
  try {
    loading.value = true
    const response = await api.get('/red-bills', {
      params: { year_start: yearStart.value, year_end: yearEnd.value }
    })
    redBills.value = response.data
  } catch (error) {
    console.error('Error loading RED bills:', error)
    redBills.value = []
  } finally {
    loading.value = false
  }
}

const loadSummary = async () => {
  try {
    summaryLoading.value = true
    const response = await api.get('/red-bills/summary', {
      params: { year_start: yearStart.value, year_end: yearEnd.value }
    })
    summaryData.value = response.data
  } catch (error) {
    console.error('Error loading summary:', error)
    summaryData.value = { categories: [], total: {}, net_total: 0 }
  } finally {
    summaryLoading.value = false
  }
}

const formatAmount = (amount) => {
  if (amount === null || amount === undefined) return '—'
  if (amount === 0) return '0.00'
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 20,
    useGrouping: true,
  }).format(amount)
}

const formatAverage = (amount) => {
  if (amount === null || amount === undefined) return '—'
  if (amount === 0) return '0.00'
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  }).format(amount)
}

const formatBillions = (amount) => {
  if (amount === null || amount === undefined) return '0.00'
  if (amount === 0) return '0.00'
  if (amount < 0) {
    return '(' + new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(amount)) + ')'
  }
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)
}

const previewPdf = async () => {
  try {
    previewing.value = true
    const response = await api.get('/red-bills/print', {
      params: { year_start: yearStart.value, year_end: yearEnd.value },
      responseType: 'blob'
    })
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
    window.open(url, '_blank')
  } catch (error) {
    console.error('Error previewing PDF:', error)
  } finally {
    previewing.value = false
  }
}

const downloadPdf = async () => {
  try {
    downloading.value = true
    const response = await api.get('/red-bills/print', {
      params: { year_start: yearStart.value, year_end: yearEnd.value },
      responseType: 'blob'
    })
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `red-bills-${yearStart.value}-${yearEnd.value}.pdf`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error downloading PDF:', error)
  } finally {
    downloading.value = false
  }
}

onMounted(() => {
  loadRedBills()
  loadSummary()
})

watch([yearStart, yearEnd], () => {
  if (yearStart.value && yearEnd.value && yearStart.value <= yearEnd.value) {
    loadRedBills()
    loadSummary()
  }
})
</script>

<style scoped>
.page { padding: 1rem 0; background: var(--p-surface-50); min-height: 100vh; }
.container { max-width: 100%; padding: 0 1.5rem; }
.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; padding-bottom: 1rem; border-bottom: 2px solid #dc2626; }
.page-header h1 { color: #dc2626; font-size: 2rem; font-weight: 700; margin: 0; }
.header-actions { display: flex; gap: 0.75rem; }
.filter-controls { display: flex; align-items: flex-end; gap: 1rem; margin-bottom: 1.5rem; padding: 1rem; background: white; border-radius: 8px; border: 1px solid #e5e7eb; }

.tab-controls { display: flex; gap: 0.5rem; margin-bottom: 1.5rem; }

.summary-section { background: white; border-radius: 8px; padding: 1.5rem; border: 1px solid #e5e7eb; }
.summary-loading { text-align: center; padding: 2rem; color: #6b7280; }
.summary-table { width: 100%; border-collapse: collapse; }
.summary-table th { background: #1e3a5c; color: white; padding: 0.75rem 1rem; text-align: center; font-size: 0.85rem; font-weight: 600; border: 1px solid #2d4a6f; }
.summary-table td { padding: 0.6rem 1rem; border: 1px solid #d1d5db; font-size: 0.9rem; }
.summary-table .group-header td { background: #f8fafc; font-weight: 700; padding: 0.5rem 1rem; }
.summary-table .total-header td { background: #fef3c7; font-weight: 700; }
.summary-table .total-row td { background: #fffbeb; }
.summary-table .net-total-row td { background: #fff1f2; font-weight: 700; }
.summary-table .indent { padding-left: 2rem; }
.summary-table .center { text-align: center; }
.summary-table .right { text-align: right; }
.summary-table .negative { color: #dc2626; font-weight: 600; }
.summary-table .net-total-value { font-size: 1rem; }
.filter-group { display: flex; flex-direction: column; gap: 0.25rem; }
.filter-group label { font-size: 0.8rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; }
.search-group { flex: 1; }
.search-group :deep(.p-inputtext) { width: 100%; }
.subject-title { font-weight: 600; color: #1f2937; }
.pp-links { display: flex; flex-direction: column; gap: 0.5rem; }
.pp-item { display: flex; flex-direction: column; gap: 0.15rem; padding-bottom: 0.4rem; border-bottom: 1px solid #f3f4f6; }
.pp-item:last-child { border-bottom: none; padding-bottom: 0; }
.pp-title { font-size: 0.8rem; font-weight: 600; color: #1f2937; }
.pp-link-row { display: flex; gap: 0.5rem; }
.pp-link { display: inline-flex; align-items: center; gap: 0.25rem; font-size: 0.8rem; color: #2563eb; text-decoration: none; }
.pp-link:hover { color: #1d4ed8; text-decoration: underline; }
.pp-link.signed { color: #16a34a; }
.pp-link.signed:hover { color: #15803d; }
.category-tag { display: inline-block; padding: 0.15rem 0.5rem; margin: 0.1rem; font-size: 0.75rem; background: #fef2f2; color: #dc2626; border-radius: 4px; border: 1px solid #fecaca; }
.amount-cell { text-align: right; font-weight: 500; color: #374151; font-variant-numeric: tabular-nums; }
.sum-cell { font-weight: 700; color: #1f2937; }
.fiscal-total { font-weight: 700; color: #dc2626; }
.no-data { color: #9ca3af; }
.particulars-list { display: flex; flex-direction: column; gap: 0.5rem; }
.particulars-item { display: flex; flex-direction: column; gap: 0.2rem; }
.source-tag { display: inline-block; padding: 0.1rem 0.4rem; font-size: 0.65rem; font-weight: 700; border-radius: 3px; text-transform: uppercase; letter-spacing: 0.03em; width: fit-content; }
.source-house { background: #dbeafe; color: #1e40af; }
.source-senate { background: #fef3c7; color: #92400e; }
.source-pllo { background: #e0e7ff; color: #3730a3; }
.source-odesla { background: #dcfce7; color: #166534; }
.particulars-text { font-size: 0.8rem; color: #374151; line-height: 1.3; }

:deep(.red-bills-table .p-datatable-header-cell) {
  background: #fef2f2 !important;
  color: #991b1b !important;
  font-weight: 700;
  font-size: 0.8rem;
  text-align: center !important;
}
</style>
